// src/redux/slices/authSlice.ts

import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { loginUser, loginUserNew, forgotPassword, resetPassword, verifyOTP } from "../thunks/authThunks";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null;
  loading: boolean;
  error: string | null;
  forgotPasswordLoading: boolean;
  forgotPasswordError: string | null;
  resetPasswordLoading: boolean;
  resetPasswordError: string | null;
  otpVerificationLoading: boolean;
  otpVerificationError: string | null;
  forgotPasswordSuccess: boolean;
  resetPasswordSuccess: boolean;
  otpVerificationSuccess: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  user: null,
  loading: false,
  error: null,
  forgotPasswordLoading: false,
  forgotPasswordError: null,
  resetPasswordLoading: false,
  resetPasswordError: null,
  otpVerificationLoading: false,
  otpVerificationError: null,
  forgotPasswordSuccess: false,
  resetPasswordSuccess: false,
  otpVerificationSuccess: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.error = null;
      // Remove token from localStorage
      localStorage.removeItem('token');
    },
    clearErrors: (state) => {
      state.error = null;
      state.forgotPasswordError = null;
      state.resetPasswordError = null;
      state.otpVerificationError = null;
    },
    clearSuccessStates: (state) => {
      state.forgotPasswordSuccess = false;
      state.resetPasswordSuccess = false;
      state.otpVerificationSuccess = false;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Legacy login thunk
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        // Transform legacy user structure to new structure
        state.user = {
          id: '1', // Default ID for legacy login
          email: action.payload.user.email,
          name: action.payload.user.name,
          role: 'admin', // Default role for legacy login
          avatar: undefined
        };
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // New login thunk
    builder
      .addCase(loginUserNew.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUserNew.fulfilled, (state, action) => {
        const data :any = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = data?.data?.token;
        state.user = data?.data?.admin;
        // Store token in localStorage
        localStorage.setItem('token', data?.data?.token);
      })
      .addCase(loginUserNew.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Forgot password thunk
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.forgotPasswordError = null;
        state.forgotPasswordSuccess = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = true;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordError = action.payload as string;
      });

    // Reset password thunk
    builder
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordError = action.payload as string;
      });

    // Verify OTP thunk
    builder
      .addCase(verifyOTP.pending, (state) => {
        state.otpVerificationLoading = true;
        state.otpVerificationError = null;
        state.otpVerificationSuccess = false;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.otpVerificationLoading = false;
        state.otpVerificationSuccess = true;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.otpVerificationLoading = false;
        state.otpVerificationError = action.payload as string;
      });
  },
});

export const { logout, clearErrors, clearSuccessStates, updateUser } = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectForgotPasswordLoading = (state: RootState) => state.auth.forgotPasswordLoading;
export const selectForgotPasswordError = (state: RootState) => state.auth.forgotPasswordError;
export const selectForgotPasswordSuccess = (state: RootState) => state.auth.forgotPasswordSuccess;
export const selectResetPasswordLoading = (state: RootState) => state.auth.resetPasswordLoading;
export const selectResetPasswordError = (state: RootState) => state.auth.resetPasswordError;
export const selectResetPasswordSuccess = (state: RootState) => state.auth.resetPasswordSuccess;
export const selectOTPVerificationLoading = (state: RootState) => state.auth.otpVerificationLoading;
export const selectOTPVerificationError = (state: RootState) => state.auth.otpVerificationError;
export const selectOTPVerificationSuccess = (state: RootState) => state.auth.otpVerificationSuccess;

export default authSlice.reducer;
